import axios from 'axios';

// Get backend URL from environment
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use(
  (config) => {
    // Check for both regular token and admin token
    const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// === PRODUCTS ===
export const getProducts = async (category = null, featured = null) => {
  try {
    const params = {};
    if (category && category !== 'todos') params.category = category;
    if (featured !== null) params.featured = featured;
    
    const response = await api.get('/api/products/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

export const getProduct = async (productId) => {
  try {
    const response = await api.get(`/api/products/${productId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

export const getFeaturedProducts = async () => {
  try {
    const response = await api.get('/api/products/featured');
    return response.data;
  } catch (error) {
    console.error('Error fetching featured products:', error);
    throw error;
  }
};

export const createProduct = async (productData) => {
  try {
    const response = await api.post('/api/products/', productData);
    return response.data;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

export const updateProduct = async (productId, productData) => {
  try {
    const response = await api.put(`/api/products/${productId}`, productData);
    return response.data;
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

export const deleteProduct = async (productId) => {
  try {
    const response = await api.delete(`/api/products/${productId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

// === CATEGORIES ===
export const getCategories = async () => {
  try {
    const response = await api.get('/api/categories/');
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

export const updateCategoryImage = async (categoryId, data) => {
  try {
    console.log(`API: Updating category ${categoryId}`, data);
    const response = await api.put(`/api/categories/${categoryId}/image`, data);
    console.log('API: Category update successful', response.data);
    return response.data;
  } catch (error) {
    console.error('API: Error updating category:', error.response?.data || error.message);
    throw error;
  }
};

export const toggleProductAvailability = async (productId, available) => {
  try {
    const response = await api.patch(`/api/products/${productId}/availability?available=${available}`);
    return response.data;
  } catch (error) {
    console.error('Error toggling product availability:', error);
    throw error;
  }
};

// === SEARCH CONFIG ===
export const getSearchConfig = async () => {
  try {
    const response = await api.get('/api/search/config');
    return response.data;
  } catch (error) {
    console.error('Error fetching search config:', error);
    throw error;
  }
};

export const updateSearchConfig = async (configData) => {
  try {
    const response = await api.put('/api/search/config', configData);
    return response.data;
  } catch (error) {
    console.error('Error updating search config:', error);
    throw error;
  }
};

// === HERO BLOCKS ===
export const getHeroBlocks = async () => {
  try {
    const response = await api.get('/api/hero-blocks/');
    return response.data;
  } catch (error) {
    console.error('Error fetching hero blocks:', error);
    throw error;
  }
};

export const updateHeroBlock = async (blockId, data) => {
  try {
    const response = await api.put(`/api/hero-blocks/${blockId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating hero block:', error);
    throw error;
  }
};

// === SEARCH RECOMMENDATIONS ===
export const getSearchRecommendations = async () => {
  try {
    const response = await api.get('/api/search-recommendations/');
    return response.data;
  } catch (error) {
    console.error('Error fetching search recommendations:', error);
    throw error;
  }
};

export const getAllSearchRecommendations = async () => {
  try {
    const response = await api.get('/api/search-recommendations/all');
    return response.data;
  } catch (error) {
    console.error('Error fetching all search recommendations:', error);
    throw error;
  }
};

export const createSearchRecommendation = async (data) => {
  try {
    const response = await api.post('/api/search-recommendations/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating search recommendation:', error);
    throw error;
  }
};

export const updateSearchRecommendation = async (recommendationId, data) => {
  try {
    const response = await api.put(`/api/search-recommendations/${recommendationId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating search recommendation:', error);
    throw error;
  }
};

export const deleteSearchRecommendation = async (recommendationId) => {
  try {
    const response = await api.delete(`/api/search-recommendations/${recommendationId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting search recommendation:', error);
    throw error;
  }
};

// === LANDING CONFIGS (FONDOS) ===
export const getLandingConfigs = async () => {
  try {
    const response = await api.get('/api/landing-configs/');
    return response.data;
  } catch (error) {
    console.error('Error fetching landing configs:', error);
    throw error;
  }
};

export const getAllLandingConfigs = async () => {
  try {
    const response = await api.get('/api/landing-configs/all');
    return response.data;
  } catch (error) {
    console.error('Error fetching all landing configs:', error);
    throw error;
  }
};

export const getLandingConfig = async (configId) => {
  try {
    const response = await api.get(`/api/landing-configs/${configId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching landing config:', error);
    throw error;
  }
};

export const createLandingConfig = async (data) => {
  try {
    const response = await api.post('/api/landing-configs/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating landing config:', error);
    throw error;
  }
};

export const updateLandingConfig = async (configId, data) => {
  try {
    const response = await api.put(`/api/landing-configs/${configId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating landing config:', error);
    throw error;
  }
};

export const deleteLandingConfig = async (configId) => {
  try {
    const response = await api.delete(`/api/landing-configs/${configId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting landing config:', error);
    throw error;
  }
};

// === AUTHENTICATION ===
export const login = async (email, password) => {
  try {
    const response = await api.post('/api/auth/login', { email, password });
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

export const logout = async () => {
  try {
    await api.post('/api/auth/logout');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await api.get('/api/auth/me');
    return response.data;
  } catch (error) {
    console.error('Error fetching current user:', error);
    throw error;
  }
};

// === ORDERS ===
export const createOrder = async (orderData) => {
  try {
    const response = await api.post('/api/orders/', orderData);
    return response.data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

export const getOrders = async () => {
  try {
    const response = await api.get('/api/orders/');
    return response.data;
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

export const getOrder = async (orderId) => {
  try {
    const response = await api.get(`/api/orders/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
};

export const updateOrderStatus = async (orderId, status) => {
  try {
    const response = await api.put(`/api/orders/${orderId}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

// === HEALTH CHECK ===
export const healthCheck = async () => {
  try {
    const response = await api.get('/api/health');
    return response.data;
  } catch (error) {
    console.error('Error checking health:', error);
    throw error;
  }
};

// === PORTADAS ===
export const getAllPortadas = async () => {
  try {
    const response = await api.get('/api/portadas/');
    return response.data;
  } catch (error) {
    console.error('Error fetching portadas:', error);
    throw error;
  }
};

export const getPortada = async (portadaId) => {
  try {
    const response = await api.get(`/api/portadas/${portadaId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching portada:', error);
    throw error;
  }
};

export const updatePortada = async (portadaId, portadaData) => {
  try {
    const response = await api.put(`/api/portadas/${portadaId}`, portadaData);
    return response.data;
  } catch (error) {
    console.error('Error updating portada:', error);
    throw error;
  }
};

// === LOGO ===
export const getLogo = async () => {
  try {
    const response = await api.get('/api/logo/');
    return response.data;
  } catch (error) {
    console.error('Error fetching logo:', error);
    throw error;
  }
};

export const updateLogo = async (logoData) => {
  try {
    const response = await api.put('/api/logo/', logoData);
    return response.data;
  } catch (error) {
    console.error('Error updating logo:', error);
    throw error;
  }
};

export default api;
